import { useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import { useAuth } from "@/store/AuthStore";
import SectionTitle from "@/components/base/SectionTitle";
import InventoryStats from "@/pages/admin/inventario/components/InventoryStats";
import InventoryMatrix from "@/pages/admin/inventario/components/InventoryMatrix";
import InventoryEditModal from "@/pages/admin/inventario/components/InventoryEditModal";
import InventoryAdjustmentHistory from "@/pages/admin/inventario/components/InventoryAdjustmentHistory";
import PurchaseToStockModal from "@/pages/admin/contabilidad/components/PurchaseToStockModal";
import { buildFunds, buildPriceAlerts } from "@/utils/accounting";
import { resolveCategories } from "@/utils/categories";
import type { InventoryItem, Product } from "@/types";

const InventoryPage = () => {
  const { data, addPurchaseToStock, applyInventoryAdjustment, transferInventoryStock } = useApp();
  const { profile } = useAuth();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("Todas");
  const [editing, setEditing] = useState<Product | null>(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyProductId, setBuyProductId] = useState<string | undefined>(undefined);
  const [feedback, setFeedback] = useState("");
  const [feedbackKind, setFeedbackKind] = useState<"ok" | "error">("ok");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.products.filter((p) => {
      const matchCategory = category === "Todas" || p.category === category;
      const matchQuery =
        !q || p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q);
      return matchCategory && matchQuery;
    });
  }, [data.products, query, category]);

  const categoryFilters = useMemo(
    () => ["Todas", ...resolveCategories(data.settings.categories)],
    [data.settings.categories]
  );

  // Avisos de desfase de precio (costo ≥ precio público o cuenta del asesor que
  // no cuadra). Se marcan en ámbar al editar el inventario del producto.
  const alerts = useMemo(() => buildPriceAlerts(data), [data]);
  const cajaBalance = useMemo(
    () => buildFunds(data).funds.find((f) => f.key === "caja")?.balance ?? 0,
    [data]
  );
  const editingAlert = useMemo(
    () => (editing ? alerts.find((a) => a.productId === editing.id) ?? null : null),
    [alerts, editing]
  );

  // Productos con una compra pendiente de pago (se atenúan en rojo en la matriz).
  const pendingProductIds = useMemo(
    () =>
      data.expenses
        .filter((e) => e.kind === "compra" && e.status !== "pagado" && e.productId)
        .map((e) => e.productId as string),
    [data.expenses]
  );

  const handleAdjust = (item: InventoryItem, reason: string) => {
    if (!editing) return;
    const before = data.inventory.find((i) => i.productId === item.productId)?.stockCasa ?? 0;
    const after = item.stockCasa;
    if (after === before) {
      setEditing(null);
      return;
    }
    applyInventoryAdjustment(item, {
      id: "",
      date: new Date().toISOString(),
      productId: item.productId,
      productName: editing.name,
      qtyBefore: before,
      qtyAfter: after,
      delta: after - before,
      reason: reason || "Ajuste de inventario",
      userName: profile ? `${profile.names} ${profile.surnames}`.trim() || profile.email : "Administrador",
      userId: profile?.id,
    });
    setEditing(null);
    setFeedbackKind("ok");
    setFeedback("Ajuste de inventario registrado con su constancia.");
    setTimeout(() => setFeedback(""), 2600);
  };

  // Traspaso de unidades entre dos productos (no toca movimientos ni contabilidad).
  const handleTransfer = async (target: Product, qty: number, reason: string) => {
    if (!editing) return;
    try {
      await transferInventoryStock({
        sourceId: editing.id,
        sourceName: editing.name,
        targetId: target.id,
        targetName: target.name,
        qty,
        reason,
        userName: profile
          ? `${profile.names} ${profile.surnames}`.trim() || profile.email
          : "Administrador",
        userId: profile?.id,
      });
      setEditing(null);
      setFeedbackKind("ok");
      setFeedback(`Se traspasaron ${qty} und de “${editing.name}” a “${target.name}”.`);
      setTimeout(() => setFeedback(""), 3200);
    } catch (e) {
      setFeedbackKind("error");
      setFeedback(e instanceof Error ? e.message : "No se pudo hacer el traspaso.");
      setTimeout(() => setFeedback(""), 4200);
    }
  };

  const handleRequestBuy = (product: Product) => {
    setBuyProductId(product.id);
    setEditing(null);
    setBuyOpen(true);
  };

  const handlePurchase = (input: Parameters<typeof addPurchaseToStock>[0]) => {
    addPurchaseToStock(input);
    setBuyOpen(false);
    setBuyProductId(undefined);
    setFeedbackKind("ok");
    setFeedback("Compra registrada y stock ingresado a Oficina.");
    setTimeout(() => setFeedback(""), 2600);
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Inventario Físico Consolidado"
        subtitle="Matriz por categoría con stock en Oficina (admin) y consignación por asesor, con valoraciones y totales en tiempo real."
      />

      <InventoryStats
        products={data.products}
        inventory={data.inventory}
        distributors={data.distributors}
      />

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mt-5 mb-4">
        <div className="relative flex-1 max-w-md">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
            <i className="ri-search-line" />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre o SKU..."
            className="w-full bg-background-100/70 border border-background-300/50 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/50 focus:ring-2 focus:ring-accent-500/25"
            aria-label="Buscar producto en inventario"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {categoryFilters.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border cursor-pointer whitespace-nowrap transition-colors ${
                category === c
                  ? "bg-accent-500/20 border-accent-500/40 text-accent-200"
                  : "border-background-300/50 text-foreground-600 hover:border-accent-500/30"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <button
          onClick={() => setBuyOpen(true)}
          className="px-3.5 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
        >
          <i className="ri-add-line" /> Comprar para stock
        </button>
      </div>

      {feedback && (
        <div
          className={`mb-3 rounded-md glass-panel px-4 py-2.5 flex items-center gap-2 animate-fade-up border ${
            feedbackKind === "error"
              ? "border-primary-500/40 bg-primary-500/10"
              : "border-accent-500/30"
          }`}
        >
          <i
            className={`${
              feedbackKind === "error"
                ? "ri-error-warning-line text-primary-200"
                : "ri-checkbox-circle-line text-accent-300"
            }`}
          />
          <p className="text-xs text-foreground-800">{feedback}</p>
        </div>
      )}

      {pendingProductIds.length > 0 && (
        <div className="mb-3 rounded-md bg-primary-500/10 border border-primary-500/40 px-4 py-2.5 flex items-center gap-2">
          <i className="ri-alert-line text-primary-200" />
          <p className="text-xs text-foreground-800">
            Hay {pendingProductIds.length} producto(s) con compra pendiente de pago. Se resaltan en
            rojo; cancela el pendiente en Contabilidad.
          </p>
        </div>
      )}

      <InventoryMatrix
        products={filtered}
        inventory={data.inventory}
        distributors={data.distributors}
        pendingProductIds={pendingProductIds}
        onEdit={setEditing}
      />

      <InventoryEditModal
        open={editing !== null}
        product={editing}
        inventory={data.inventory}
        products={data.products}
        alert={editingAlert}
        onClose={() => setEditing(null)}
        onAdjust={handleAdjust}
        onRequestBuy={handleRequestBuy}
        onTransfer={handleTransfer}
      />

      <div className="mt-5">
        <InventoryAdjustmentHistory adjustments={data.inventoryAdjustments} />
      </div>

      <PurchaseToStockModal
        open={buyOpen}
        products={data.products}
        cajaBalance={cajaBalance}
        initialProductId={buyProductId}
        onClose={() => {
          setBuyOpen(false);
          setBuyProductId(undefined);
        }}
        onSubmit={handlePurchase}
      />
    </div>
  );
};

export default InventoryPage;